import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { nanoid } from 'nanoid';
import { motion, AnimatePresence } from 'motion/react';
import { useSession } from '../store/session';
import ModeSelector, { DirectorSelector } from './ModeSelector';
import ModelSelector from './ModelSelector';
import AspectRatio from './AspectRatio';
import DurationPicker from './DurationPicker';
import { startGeneration, pollUntilDone } from '../router/orchestrator';
import { chatWithDirector } from '../router/director';
import { useCodeProject } from '../code/useCodeProject';
import { codingSystemPrompt, parseCodeFiles } from '../code/codeAgent';
import type { Attachment, ChatMessage } from '../lib/db';
import { extractEndpoints } from '../media/frame-extract';
import { getKey } from '../lib/keys';
import { proxy } from '../providers/common';
import { buildSyntheticSoundWav } from '../providers/elevenlabs';

const VIDEO_PRESETS: Record<'FX' | 'Camera' | 'Lighting' | 'Motion' | 'Style', { label: string; inject: string }[]> = {
  FX: [
    { label: "Smoke & Haze", inject: "volumetric dust and drifting heavy gray smoke" },
    { label: "Roaring Fire", inject: "dancing cinematic fire tongues with realistic orange heat embers" },
    { label: "Viscous Liquid", inject: "sluggishly flowing viscous glossy thick slime and fluid simulation" },
    { label: "De-atomization", inject: "decaying particle de-atomization, dissolving into glowing energy dust" },
    { label: "Water Splash", inject: "hyper-detailed slow motion splashing water droplets" },
    { label: "Spark Rain", inject: "showering bright metallic welding sparks reflecting on surfaces" },
    { label: "Glitch & Distortion", inject: "cybernetic digital pixel stabilization glitch and scanline distortion" },
    { label: "Ink in Water", inject: "billowing dark ink droplets dispersing elegantly in water" },
    { label: "Portal Sparkle", inject: "shimmering spatial dimensional rift emitting bright magical plasma particles" },
    { label: "Shockwave Ripple", inject: "expanding supersonic soundwave displacement atmospheric ripple" },
    { label: "Fog Nebula", inject: "dense low-lying mystical ground fog creeping quietly" },
    { label: "Energy Shield", inject: "pulsing hexagonal soft blue holographic forcefield energy barrier" },
    { label: "Sand Dust Storm", inject: "swirling wind-blown granular desert sand and dust particles" },
    { label: "Bubble Stream", inject: "rising streams of iridescent gaseous water bubbles" },
    { label: "Melting Slime", inject: "slowly liquefying and dripping molten neon wax slime" },
    { label: "Crystal Shards", inject: "shattering glass and reflecting light-refracting crystal shards" },
    { label: "Frost Growth", inject: "crawling crystalline ice frost patterns freezing across the canvas" },
    { label: "Plasma Discharge", inject: "snaking electric arcs of intense blue plasma static discharge" },
    { label: "Smoke Plume Ring", inject: "vortex puff of dense smoke rings inflating expandingly" },
    { label: "Cosmic Aura", inject: "swirling colorful cosmic stardust and nebula particulate clouds" }
  ],
  Camera: [
    { label: "Low Angle (Hero)", inject: "low-angle cinematic hero view, towering perspective" },
    { label: "High Angle (Bird's Eye)", inject: "downward-looking dramatic birds-eye high angle perspective" },
    { label: "Extreme Close Up", inject: "extreme macro close-up, hyper-focused shallow depth of field" },
    { label: "Over The Shoulder", inject: "over-the-shoulder classic frame, tracking perspective" },
    { label: "Dutch Angle", inject: "tilted Dutch angle, dynamic off-kilter tension showcase" },
    { label: "Wide Wide Shot", inject: "sweeping extreme wide landscape shot, majestic scale" },
    { label: "Eye Level", inject: "neutral intimate eye-level documentary frame" },
    { label: "Ground Level", inject: "ground-level worm-eye view crawling perspective" },
    { label: "Split Diopter", inject: "split-diopter depth, crisp near foreground and sharp background parallel focus" },
    { label: "Snorricam", inject: "actor-mounted snorricam body-locked perspective, extreme personal kinetic view" },
    { label: "Point of View", inject: "first-person subjective POV camera perspective" },
    { label: "Silhouette Backlit", inject: "silhouette perspective backlit by heavy key illumination source" },
    { label: "Worm's Eye View", inject: "looking straight up from floor plane vertically" },
    { label: "Two-Shot", inject: "medium two-shot, balanced twin character framing" },
    { label: "Telephoto Macro", inject: "compressed long lens telephoto perspective, isolates subject" },
    { label: "Low-Flying Drone", inject: "low-altitude skimming drone aerial camera view" },
    { label: "High Horizon", inject: "high horizon framing, emphasizes rich detailed foreground terrain" },
    { label: "Low Horizon", inject: "low horizon framing, emphasizes sweeping dramatic sky and cosmos" },
    { label: "Inside-Out Shot", inject: "looking from within a box or cavity outwards toward light source" },
    { label: "Chiaroscuro Macro", inject: "dramatic contrast macro shot, isolating bright edges against deep dark pools" }
  ],
  Lighting: [
    { label: "Golden Hour", inject: "bathed in warm late-afternoon golden hour solar rays" },
    { label: "Volumetric God Rays", inject: "divine volumetric light shafts filtering through dense dusty air" },
    { label: "Neon Cyberpunk", inject: "high-contrast saturated cyan and magenta cyberpunk neon glow" },
    { label: "Rembrandt", inject: "Rembrandt lighting style, soft 45-degree key light producing distinct facial shadow triangles" },
    { label: "Backlight / Rim", inject: "intense rim lighting halo glow outlining silhouettes" },
    { label: "Bioluminescent Glow", inject: "ethereal soft organic bioluminescent neon blue-green glow" },
    { label: "Strobe / Flickering", inject: "atmospheric stuttering strobe flashes casting high-contrast shadows" },
    { label: "Overcast Diffusion", inject: "flat overcast natural diffused daylight, shadowless realism" },
    { label: "Candlelight", inject: "warm organic flickering candlelight source, cozy deep shadows" },
    { label: "Anamorphic Lens Flare", inject: "cinematic anamorphic horizontal blue lens flares streaming from light point" },
    { label: "Moonlit Night", inject: "cool atmospheric silver moonlit blue-gray night glow" },
    { label: "High-Key Studio", inject: "pristine high-key flawless white studio commercial lighting" },
    { label: "Low-Key Noir", inject: "mysterious low-key film noir setup, stark hard shadows and deep blacks" },
    { label: "Prism Refraction", inject: "rainbow-tinted light rays refracted through raw glass prisms" },
    { label: "Under-lighting", inject: "unsettling theatrical green under-lighting from ground level" },
    { label: "Firelight Glow", inject: "pulsing golden warm firelight glow reflecting dynamically" },
    { label: "Sun Flare Leak", inject: "overexposed blinding sun flare leaking directly into lens" },
    { label: "Studio Softbox", inject: "broad professional studio softbox light source, gentle roll-off" },
    { label: "Sci-fi Scan Laser", inject: "pulsing laser scanline crimson light swipe across the scene" },
    { label: "Aurora Radiance", inject: "dancing undulating waves of soft green northern lights sky glow" }
  ],
  Motion: [
    { label: "Dolly In", inject: "smooth cinematic camera dolly-in push" },
    { label: "Dolly Out", inject: "smooth cinematic camera dolly-out pull" },
    { label: "Pan Left", inject: "panning slow tracking horizontal sweep to the left" },
    { label: "Pan Right", inject: "panning slow tracking horizontal sweep to the right" },
    { label: "Track Left", inject: "sideways lateral camera track to the left" },
    { label: "Push Up", inject: "dramatic push-up crane vertical lift camera climb" },
    { label: "High Speed / Slow Mo", inject: "ultra high speed slow-motion 1000fps slow-mo camera capture" },
    { label: "Orbital Arc", inject: "spectacular 360-degree orbital arc camera sweep" },
    { label: "Tilt Down Reveal", inject: "slow vertical tilt-down camera reveal" },
    { label: "Parallax Tracking", inject: "gorgeous lateral side-scroller tracking shot with deep background parallax movement" },
    { label: "Handheld Jitter", inject: "visceral gritty low-amplitude handheld camera shake" },
    { label: "Zoom-out Spiral", inject: "dizzying spiraling roll zoom-out vertigo camera movement" },
    { label: "Whip Pan", inject: "extremely fast whip pan camera swish blur transition" },
    { label: "Pedestal Rise", inject: "stabilized constant speed pedestal vertical upward camera climb" },
    { label: "Crash Zoom", inject: "sudden high-speed retro-style snap crash zoom" },
    { label: "Bullet Time", inject: "frozen bullet-time rotational snapshot effect" },
    { label: "Steadicam Follow", inject: "fluid kinetic steadicam following closely behind subject" },
    { label: "Low Altitude Skim", inject: "forward high speed ground-skimming motion" },
    { label: "Vertigo Effect", inject: "vertigo dolly zoom, lens zoom in while camera moves out" },
    { label: "Pendulum Pivot", inject: "rocking pendulum pivot camera swing over horizontal center" }
  ],
  Style: [
    { label: "Retro Anime", inject: "stylized late 80s hand-drawn cellular cell retro anime aesthetic" },
    { label: "Cyberpunk Futurism", inject: "neon-infused dark futuristic cyberpunk sci-fi styling" },
    { label: "Isometric 3D", inject: "isometric high resolution orthographic stylized 3D scene" },
    { label: "Pop Art Collage", inject: "vibrant flat color screenprint pop-art collage visual style" },
    { label: "2D Vector", inject: "clean flat vector 2D digital illustration, sharp shapes" },
    { label: "Photorealistic 8K", inject: "hyper-realistic photorealistic 8k detail resolution, perfect textures" },
    { label: "Film Noir Grain", inject: "grainy 35mm film noir black-and-white high contrast aesthetic" },
    { label: "Claymation", inject: "stylized tactile stop-motion plasticine claymation look" },
    { label: "Ink Wash (Sumi-e)", inject: "traditional fluid sumi-e wash black ink calligraphic watercolor painting" },
    { label: "Synthwave Outrun", inject: "outrun synthwave retrowave wireframe grid sunset magenta styling" },
    { label: "Glitch Art", inject: "digital post-internet datamosh glitch art aesthetic" },
    { label: "Pencil Sketch", inject: "rough graphite crosshatched pencil sketchbook drawing" },
    { label: "Psychedelic 60s", inject: "vibrant trippy swirling psychedelic liquid oil 1960s art style" },
    { label: "Watercolor Bleed", inject: "soft pastel dry-brush bleeding watercolor washes" },
    { label: "Woodcut Print", inject: "rustic vintage hand-carved woodblock printing texture" },
    { label: "Bauhaus Minimalist", inject: "bold geometric primary color blocks Bauhaus design language" },
    { label: "Clay Viewport Render", inject: "unskinned pale gray procedural clay modeling software viewport render" },
    { label: "Miniature Tilt-Shift", inject: "toy-scale lens dioramas miniature tilt-shift blurred view" },
    { label: "Paper Cutout", inject: "layered colored construction paper cutout silhouette craftwork" },
    { label: "Unreal Engine 5", inject: "highly-polished raytraced Unreal Engine 5 state-of-the-art cinematic cinematic game output" }
  ]
};

const IMAGE_PRESETS: Record<'Photography' | 'Art' | 'Lighting' | 'Style', { label: string; inject: string }[]> = {
  Photography: [
    { label: "Macro Close-up", inject: "extreme macro photography, highly detailed, shallow depth of field, sharp focus" },
    { label: "Wide Angle", inject: "wide angle lens, expansive view, architectural photography style" },
    { label: "Telephoto Portrait", inject: "85mm telephoto lens portrait, creamy bokeh background, classic portraiture" },
    { label: "Drone Aerial", inject: "drone aerial top-down view, high altitude perspective, geographical" },
    { label: "Polaroid Vintage", inject: "vintage polaroid sx-70 film look, soft contrast, faded film borders" },
    { label: "Disposable Camera", inject: "90s disposable camera flash flash photography, harsh lighting, amateur aesthetic" }
  ],
  Art: [
    { label: "Oil Painting", inject: "thick impasto oil painting on canvas, visible brush strokes, rich classical art" },
    { label: "Watercolor", inject: "ethereal watercolor painting, beautiful soft blending, pastel paper texture" },
    { label: "Pencil Sketch", inject: "detailed graphite pencil sketch, cross-hatching, sketchbook paper texture" },
    { label: "Digital Illustration", inject: "clean digital illustration vector art, vibrant flat colors, crisp lines" },
    { label: "Anime Concept", inject: "studio ghibli style anime concept art background, lush details, 2D animation" },
    { label: "Ukiyo-e Woodblock", inject: "traditional japanese ukiyo-e woodblock print, flat colors, line art" }
  ],
  Lighting: [
    { label: "Golden Hour Glow", inject: "golden hour lighting, warm amber sunlight, soft long shadows" },
    { label: "Studio Softbox", inject: "professional studio softbox lighting, even smooth illumination, neutral backdrop" },
    { label: "Neon Cyberpunk", inject: "neon cyberpunk lighting, pink and cyan glowing lights, reflective rain puddles" },
    { label: "Cinematic Chiaroscuro", inject: "dramatic chiaroscuro lighting, high contrast deep blacks, stark light rays" },
    { label: "Bioluminescent", inject: "dark environment with glowing bioluminescent blue and green soft neon lights" },
    { label: "Overcast Day", inject: "flat diffused overcast day lighting, soft cloud cover, desaturated natural tones" }
  ],
  Style: [
    { label: "Minimalist", inject: "minimalist composition, vast negative space, simple geometry, clean aesthetic" },
    { label: "Maximalist Detail", inject: "maximalist incredibly dense details, chaotic but beautiful composition, highly intricate" },
    { label: "Surrealism", inject: "dreamlike surrealism, salvador dali inspired, mind-bending impossibilities" },
    { label: "Isometric 3D", inject: "isometric 3D render, octane render, tilt-shift, cute tiny world" },
    { label: "Vintage Poster", inject: "1950s retro vintage advertising poster style, halftone dots, faded paper" },
    { label: "Futuristic UI", inject: "HUD futuristic user interface overlay, hologram schematics, sci-fi concept" }
  ]
};

export default function Composer() {
  const mode = useSession(s => s.mode);
  const aspect = useSession(s => s.aspect);
  const durationSec = useSession(s => s.durationSec);
  const modelOverride = useSession(s => s.modelOverride);
  const staging = useSession(s => s.staging);
  const addStaged = useSession(s => s.addStaged);
  const removeStaged = useSession(s => s.removeStaged);
  const setStartFrame = useSession(s => s.setStartFrame);
  const setEndFrame = useSession(s => s.setEndFrame);
  const clearStaging = useSession(s => s.clearStaging);
  const append = useSession(s => s.appendMessage);
  const removeMessage = useSession(s => s.removeMessage);
  const updateMessage = useSession(s => s.updateMessage);
  const messages = useSession(s => s.messages);
  const directorAgent = useSession(s => s.directorAgent);
  const activeId = useSession(s => s.activeId);

  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const recogRef = useRef<any>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Enhance Prompt States
  const [enhanceOpen, setEnhanceOpen] = useState(false);
  const [enhanceModel, setEnhanceModel] = useState<'chatgpt' | 'grok' | 'gemini' | 'claude'>('gemini');
  const [rewrittenPrompt, setRewrittenPrompt] = useState('');
  const [enhancing, setEnhancing] = useState(false);
  const [enhanceError, setEnhanceError] = useState('');
  const [searchEnabled, setSearchEnabled] = useState(false);

  // Resolution select states
  const [imgResolution, setImgResolution] = useState<'1K' | '2K' | '4K'>('1K');
  const [videoResolution, setVideoResolution] = useState<'480P' | '720P' | '1080P' | '4K'>('1080P');

  // Video Sound Prompt state
  const [addSoundOption, setAddSoundOption] = useState(false);
  const [soundPromptOpen, setSoundPromptOpen] = useState(false);
  const [soundPromptText, setSoundPromptText] = useState('');

  // Video Modifiers States
  const [videoModOpen, setVideoModOpen] = useState(false);
  const [activeModCategory, setActiveModCategory] = useState<'FX' | 'Camera' | 'Lighting' | 'Motion' | 'Style' | 'Photography' | 'Art'>('FX');
  const [customModValue, setCustomModValue] = useState('');
  const [enhancingMod, setEnhancingMod] = useState(false);
  const [modEnhanceModel, setModEnhanceModel] = useState<'gemini' | 'chatgpt' | 'grok' | 'claude'>('gemini');
  const [presetNotification, setPresetNotification] = useState('');
  const [expandedText, setExpandedText] = useState(false);

  async function enhanceCustomMod() {
    if (!customModValue.trim()) return;
    setEnhancingMod(true);
    try {
      const rewritten = await fetchRewrite(`Cinematic ${activeModCategory}: ${customModValue}`, modEnhanceModel);
      setCustomModValue(rewritten);
    } catch (e) {
      console.error(e);
    } finally {
      setEnhancingMod(false);
    }
  }

  function handleAddTagToPrompt(tagLabel: string) {
    setText(prev => {
      let clean = prev.trim();
      // append elegantly as a tag
      const tag = `[${activeModCategory}: ${tagLabel}]`;
      if (clean.includes(tag)) {
        return clean.replace(tag, '').replace(/\s*,\s*$/, '').trim();
      }
      if (clean) {
        if (!clean.endsWith(',') && !clean.endsWith('.')) clean += ',';
        return `${clean} ${tag}`.trim();
      }
      return tag;
    });
    setPresetNotification('Saved!');
    setTimeout(() => setPresetNotification(''), 1500);
  }

  useEffect(() => {
    const h = (e: Event) => {
      const ce = e as CustomEvent<string>;
      setText(t => (t ? `${t}\n\n${ce.detail}` : ce.detail));
    };
    window.addEventListener('protolive:insert-prompt', h as EventListener);
    return () => window.removeEventListener('protolive:insert-prompt', h as EventListener);
  }, []);

  function onPickFile() { fileRef.current?.click(); }
  async function onFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    for (const f of files) {
      const kind: Attachment['kind'] = f.type.startsWith('video') ? 'video' : f.type.startsWith('audio') ? 'audio' : 'image';
      const att: Attachment = { id: nanoid(), kind, mime: f.type, blob: f, name: f.name };
      addStaged(att);
      if (kind === 'video' && mode === 'video') {
        try {
          const { start, end } = await extractEndpoints(f);
          setStartFrame({ id: nanoid(), kind: 'image', mime: 'image/png', blob: start });
          setEndFrame({ id: nanoid(), kind: 'image', mime: 'image/png', blob: end });
        } catch { /* ignore */ }
      }
    }
    e.target.value = '';
  }

  function toggleVoice() {
    const SpeechRec = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;
    if (!SpeechRec) { alert('Voice input not supported in this browser.'); return; }
    if (recording) { recogRef.current?.stop(); setRecording(false); return; }
    const r = new SpeechRec();
    r.continuous = true;
    r.interimResults = true;
    r.lang = 'en-US';
    r.onresult = (ev: any) => {
      let s = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) s += ev.results[i][0].transcript;
      setText(prev => prev + s);
    };
    r.onend = () => setRecording(false);
    recogRef.current = r;
    setRecording(true);
    r.start();
  }

  function speak(s: string) {
    if (!('speechSynthesis' in window)) return;
    const u = new SpeechSynthesisUtterance(s);
    u.rate = 1.05; u.pitch = 0.95;
    speechSynthesis.speak(u);
  }

  // Enhance prompt engine handler
  async function fetchRewrite(originalText: string, model: string): Promise<string> {
    const geminiKey = await getKey('gemini');
    const openaiKey = await getKey('openai');
    const xaiKey = await getKey('xai');
    
    const seed = originalText.trim() || 'A majestic fantasy valley';
    const systemPrompt = `You are a cinematic prompt engineer. Your sole job is to rewrite the user's messy, short, or unpolished input into an incredibly descriptive, high-fidelity, polished, and detailed model-ready prompt for ${mode} generation. 
Keep the rewrite focused on the exact core concept the user described, but expand on details like style, camera angles, lighting, textures, audio atmospheres, or 3D polygon mesh characteristics (suitable for ${mode} models).
OUTPUT ONLY THE COHESIVE, ENHANCED PROMPT. DO NOT INCLUDE ANY PREAMBLE, DISCUSSION, CONVERSATIONS, EXPLANATIONS, OR MARKDOWN BACKTICKS.`;

    const userMsg = `Enhance this prompt: "${seed}"`;

    if (model === 'gemini' && geminiKey) {
      try {
        const res = await proxy<any>({
          provider: 'gemini',
          endpoint: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent',
          body: {
            systemInstruction: { role: 'system', parts: [{ text: systemPrompt }] },
            contents: [{ role: 'user', parts: [{ text: userMsg }] }],
            generationConfig: { temperature: 0.7, maxOutputTokens: 256 }
          },
          apiKey: geminiKey,
          authStyle: 'x-goog-api-key'
        });
        return res.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || seed;
      } catch (e) {
        console.error('Gemini rewrite error', e);
      }
    }

    if (model === 'chatgpt' && openaiKey) {
      try {
        const res = await proxy<any>({
          provider: 'openai',
          endpoint: 'https://api.openai.com/v1/chat/completions',
          body: {
            model: 'gpt-4o-mini',
            messages: [
              { role: 'system', content: systemPrompt },
              { role: 'user', content: userMsg }
            ],
            temperature: 0.7,
            max_tokens: 256
          },
          apiKey: openaiKey
        });
        return res.choices?.[0]?.message?.content?.trim() || seed;
      } catch (e) {
        console.error('OpenAI rewrite error', e);
      }
    }

    if (model === 'grok' && xaiKey) {
      try {
        const res = await proxy<any>({
          provider: 'xai',
          endpoint: 'https://api.x.ai/v1/chat/completions',
          body: {
            model: 'grok-beta',
            messages: [
              { role: 'system', content: systemPrompt },
              { role: 'user', content: userMsg }
            ],
            temperature: 0.7,
            max_tokens: 256
          },
          apiKey: xaiKey
        });
        return res.choices?.[0]?.message?.content?.trim() || seed;
      } catch (e) {
        console.error('xAI rewrite error', e);
      }
    }

    // Try a universal helper API fallback if another key is active
    if (geminiKey) {
      try {
        const res = await proxy<any>({
          provider: 'gemini',
          endpoint: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent',
          body: {
            systemInstruction: { role: 'system', parts: [{ text: `${systemPrompt} (Generating on behalf of ${model})` }] },
            contents: [{ role: 'user', parts: [{ text: userMsg }] }],
            generationConfig: { temperature: 0.7, maxOutputTokens: 256 }
          },
          apiKey: geminiKey,
          authStyle: 'x-goog-api-key'
        });
        return res.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || seed;
      } catch {}
    } else if (openaiKey) {
      try {
        const res = await proxy<any>({
          provider: 'openai',
          endpoint: 'https://api.openai.com/v1/chat/completions',
          body: {
            model: 'gpt-4o-mini',
            messages: [
              { role: 'system', content: `${systemPrompt} (Generating on behalf of ${model})` },
              { role: 'user', content: userMsg }
            ],
            temperature: 0.7,
            max_tokens: 256
          },
          apiKey: openaiKey
        });
        return res.choices?.[0]?.message?.content?.trim() || seed;
      } catch {}
    }

    // Local procedural fallback
    return getLocalHeuristicRewrite(seed, model, mode);
  }

  function getLocalHeuristicRewrite(prompt: string, model: string, currentMode: string): string {
    const seed = prompt.trim() || 'beautiful scenery';
    const upModel = model.toUpperCase();
    
    if (currentMode === 'sound') {
      return `${seed} — high-fidelity stereo audio foley, immersive cinematic soundscape, 48kHz frequency, natural ambiance, crisp details, simulated via ${upModel} Sound Model.`;
    }
    if (currentMode === '3d') {
      return `${seed} — highly detailed 3D isometric mesh asset, low-poly stylized geometry, photorealistic clean textures, PBR shading, ready for Blender, 8k render style, crafted via ${upModel} Engine.`;
    }
    if (currentMode === 'video') {
      return `Cinematic shot of ${seed}. Over-the-shoulder tracking perspective, volumetric lighting scattering through atmosphere, extremely high detail 8k, raytraced shadows, hyperrealistic textures, shallow depth of field, crafted via ${upModel} Video Director.`;
    }
    
    return `Masterpiece depiction of ${seed}. Hyper-detailed digital art style, soft key lighting, atmospheric depth, volumetric gold dust, complex textures, rich high-contrast colors, unreal engine 5 render, crafted via ${upModel} Studio.`;
  }

  async function triggerEnhance(originalText: string, modelOption: typeof enhanceModel) {
    setEnhancing(true);
    setEnhanceError('');
    try {
      const rewritten = await fetchRewrite(originalText, modelOption);
      setRewrittenPrompt(rewritten);
    } catch (e: any) {
      setEnhanceError(e?.message ?? 'Enhancement error occurred.');
    } finally {
      setEnhancing(false);
    }
  }

  function onOpenEnhance() {
    setEnhanceOpen(true);
    const initialText = text.trim() || 'A beautiful cinematic scenery';
    setRewrittenPrompt(initialText);
    triggerEnhance(initialText, enhanceModel);
  }

  function onApplyRewrite() {
    setText(rewrittenPrompt);
    setEnhanceOpen(false);
  }

  const allStagedItems = [
    ...(staging.startFrame ? [{ ...staging.startFrame, role: 'start' as const }] : []),
    ...(staging.endFrame ? [{ ...staging.endFrame, role: 'end' as const }] : []),
    ...staging.attachments.map(a => ({ ...a, role: 'ref' as const }))
  ];

  function updateItemRole(
    item: Attachment,
    currentRole: 'start' | 'end' | 'ref',
    newRole: 'start' | 'end' | 'ref'
  ) {
    if (currentRole === 'ref') {
      removeStaged(item.id);
    } else if (currentRole === 'start') {
      setStartFrame(undefined);
    } else if (currentRole === 'end') {
      setEndFrame(undefined);
    }

    if (newRole === 'start') {
      if (staging.startFrame) {
        addStaged(staging.startFrame);
      }
      setStartFrame(item);
    } else if (newRole === 'end') {
      if (staging.endFrame) {
        addStaged(staging.endFrame);
      }
      setEndFrame(item);
    } else if (newRole === 'ref') {
      addStaged(item);
    }
  }

  function deleteItem(item: Attachment, role: 'start' | 'end' | 'ref') {
    if (role === 'start') {
      setStartFrame(undefined);
    } else if (role === 'end') {
      setEndFrame(undefined);
    } else {
      removeStaged(item.id);
    }
  }

  async function generateTextToSpeech() {
    if (!text.trim()) return;

    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.0;
      speechSynthesis.speak(u);
    }

    setBusy(true);
    try {
      const elKey = await getKey('elevenlabs');
      const gemKey = await getKey('gemini');
      let audioUrl = '';

      if (elKey) {
        const voiceId = '21m00Tcm4TlvDq8ikWAM';
        const url = `https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`;
        const res = await proxy<any>({
          provider: 'elevenlabs',
          endpoint: url,
          body: {
            text,
            model_id: 'eleven_monolingual_v1',
            voice_settings: { stability: 0.5, similarity_boost: 0.75 }
          },
          apiKey: elKey,
          authStyle: 'xi-api-key'
        });
        if (res?.body) audioUrl = `data:audio/mpeg;base64,${res.body}`;
        else if (res?._raw) audioUrl = `data:audio/mpeg;base64,${res._raw}`;
      } else if (gemKey) {
        const endpoint = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent`;
        const parts = [
          { text: `Generate spoken reading of the following prompt in high quality voice with excellent speech audio: "${text}"` }
        ];
        const res = await proxy<any>({
          provider: 'gemini',
          endpoint,
          body: {
            contents: [{ role: 'user', parts }],
            generationConfig: { responseMimeType: 'audio/mp3' }
          },
          apiKey: gemKey,
          authStyle: 'x-goog-api-key'
        });
        const mp3 = res.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
        if (mp3) audioUrl = `data:audio/mp3;base64,${mp3}`;
      }

      if (!audioUrl) {
        audioUrl = buildSyntheticSoundWav(text, 'tts-mic-synthesized');
      }

      const resBlob = await fetch(audioUrl);
      const blob = await resBlob.blob();
      const att: Attachment = {
        id: nanoid(),
        kind: 'audio',
        mime: blob.type || 'audio/wav',
        blob,
        name: `tts-${text.slice(0, 10).replace(/[^a-zA-Z0-9]/g, '') || 'voice'}.wav`
      };
      addStaged(att);
    } catch (e) {
      console.error('TTS attachment generation failed:', e);
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    if (!text.trim() && staging.attachments.length === 0) return;
    if (!activeId) return;

    let promptText = text.trim();
    if (window.location.pathname.startsWith('/studio')) {
      try {
        const studioState = (window as any).useEditorStore?.getState?.();
        if (studioState) {
          const clipsSummary = studioState.clips.map((c: any) => 
            `Clip(type=${c.type}, name=${c.name || 'untitled'}, start=${c.startAt}s, dur=${c.duration}s)`
          ).join(', ');
          if (clipsSummary) {
            promptText = `${promptText} [Studio Timeline status: ${clipsSummary} plays at time=${studioState.currentTime}s]`;
          } else {
            promptText = `${promptText} [Studio Timeline status: empty]`;
          }
        }
      } catch (err) {
        console.error('Failed to inject studio context:', err);
      }
    }

    if (mode === 'image' && imgResolution) {
      promptText = `${promptText} [resolution: ${imgResolution}]`;
    } else if (mode === 'video') {
      if (videoResolution) {
        promptText = `${promptText} [resolution: ${videoResolution}]`;
      }
      if (addSoundOption && soundPromptText.trim()) {
        promptText = `${promptText} [with sound fx: ${soundPromptText.trim()}]`;
      }
    }

    const userMsg: ChatMessage = {
      id: nanoid(), role: 'user', content: promptText || `[${staging.attachments.length} attachments]`,
      attachments: [...staging.attachments, ...(staging.startFrame ? [{ ...staging.startFrame, name: '[start]' }] : []), ...(staging.endFrame ? [{ ...staging.endFrame, name: '[end]' }] : [])],
      createdAt: Date.now()
    };
    await append(userMsg);
    const refs = staging.attachments
      .filter(a => a.kind === 'image')
      .map(a => ({ url: URL.createObjectURL(a.blob), mime: a.mime }));
    const refVideos = staging.attachments.filter(a => a.kind === 'video').map(a => ({ url: URL.createObjectURL(a.blob), mime: a.mime }));
    const refAudio = staging.attachments.filter(a => a.kind === 'audio').map(a => ({ url: URL.createObjectURL(a.blob), mime: a.mime }));

    const prompt = promptText;
    setText(''); clearStaging();
    setBusy(true);

    try {
      if (mode === 'chat') {
        const history = messages.slice(-10).map(m => ({ role: m.role as any, content: m.content }));
        const resp = await chatWithDirector(directorAgent, prompt, history);
        await append({ id: nanoid(), role: 'assistant', content: resp, createdAt: Date.now() });
        setBusy(false);
        return;
      }

      if (mode === 'code') {
        const code = useCodeProject.getState();
        const history = messages.slice(-10).map(m => ({ role: m.role as any, content: m.content }));
        const resp = await chatWithDirector(directorAgent, prompt, history, codingSystemPrompt(code.files));
        const written = parseCodeFiles(resp);
        for (const f of written) {
          const existing = code.files.find(x => x.name === f.path);
          code.upsertFile({ name: f.path, content: f.content, id: existing?.id ?? nanoid() });
        }
        if (written.length && !code.activePath) code.setActivePath(written[0].path);
        const note = written.length
          ? `\n\n_Wrote ${written.length} file${written.length > 1 ? 's' : ''} to the code editor: ${written.map(f => f.path).join(', ')}. Open the Code editor and hit the eye to preview._`
          : '';
        await append({ id: nanoid(), role: 'assistant', content: resp + note, meta: { model: directorAgent, provider: 'code' }, createdAt: Date.now() });
        setBusy(false);
        return;
      }

      if (mode === '3d') {
        const rev = [...messages].reverse();
        const last3DIdxRev = rev.findIndex((m: ChatMessage) => m.role === 'assistant' && (m.meta?.model === 'ProtoLive 3D' || m.attachments?.some((a: Attachment) => a.mime === 'model/gltf-binary')));
        if (last3DIdxRev !== -1) {
          const oldMsg = rev[last3DIdxRev];
          await updateMessage(oldMsg.id, (m: ChatMessage) => ({ ...m, meta: { ...m.meta, disabled: true } }));
        }
      }

      const start = await startGeneration({
        mode, prompt, aspect, durationSec,
        refs, refVideos, refAudio,
        startFrame: staging.startFrame ? { url: URL.createObjectURL(staging.startFrame.blob), mime: staging.startFrame.mime } : undefined,
        endFrame: staging.endFrame ? { url: URL.createObjectURL(staging.endFrame.blob), mime: staging.endFrame.mime } : undefined,
        modelId: modelOverride,
        sessionId: activeId,
        providerOpts: { searchEnabled }
      });

      await append({
        id: nanoid(), role: 'assistant', content: directorSummary(start.decision, start.modelId, start.provider),
        meta: { provider: start.provider, model: start.modelId, taskId: start.taskId },
        createdAt: Date.now()
      });
      speak('Brief ready. Generating now.');

      const final = start.initial.status === 'succeeded'
        ? start.initial
        : await pollUntilDone(start.taskId);

      if (final.status === 'failed') {
        await append({ id: nanoid(), role: 'assistant', content: `Generation failed: ${final.error}`, createdAt: Date.now() });
        return;
      }

      const attachments: Attachment[] = [];
      for (const a of final.assets ?? []) {
        const blob = await (await fetch(a.url)).blob();
        attachments.push({
          id: nanoid(),
          kind: a.mime.startsWith('video') ? 'video' : a.mime.startsWith('audio') ? 'audio' : 'image',
          mime: a.mime, blob, width: a.width, height: a.height, durationMs: a.durationMs
        });
      }
      await append({
        id: nanoid(), role: 'assistant',
        content: attachments.length ? 'Here is the take.' : 'Done — but no asset returned.',
        attachments,
        meta: { provider: start.provider, model: start.modelId },
        createdAt: Date.now()
      });
    } catch (e: any) {
      await append({ id: nanoid(), role: 'assistant', content: `Error: ${e?.message ?? String(e)}`, createdAt: Date.now() });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="composer glass glass--strong" style={{ padding: '8px', gap: '4px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      <div className="composer__controls" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%', padding: '0 4px', boxSizing: 'border-box', minWidth: 0 }}>
        {/* Controls Container */}
        <div className="composer__top-controls" style={{ display: 'flex', gap: '6px', width: '100%', alignItems: 'center', overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', minWidth: 0 }}>
          <button className="btn btn--icon composer__attach-mobile mobile-only" onClick={onPickFile} title="attach" style={{ flexShrink: 0, padding: 0, width: '28px', height: '28px', minHeight: 'unset', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.5 12.5 21a5 5 0 1 1-7-7l9-9a3.5 3.5 0 1 1 5 5l-9 9a2 2 0 1 1-3-3l8-8"/></svg>
          </button>
          
          <div className="composer__aspect-wrapper">
            <AspectRatio />
          </div>
          
          <div className="composer__director-wrapper">
            <DirectorSelector />
          </div>

          {mode === 'video' && (
            <>
              <div style={{ width: '1px', height: '14px', background: 'var(--line)', margin: '0 2px' }} />
              <DurationPicker />
            </>
          )}
          <AnimatePresence initial={false}>
            {(mode === 'video' || mode === 'image') && (
              <motion.div
                initial={{ opacity: 0, width: 0, scale: 0.95 }}
                animate={{ opacity: 1, width: 'auto', scale: 1 }}
                exit={{ opacity: 0, width: 0, scale: 0.95 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}
              >
                <div style={{ width: '1px', height: '14px', background: 'var(--line)', margin: '0 2px' }} />
                <button 
                  type="button"
                  className="chip composer__preset-btn" 
                  style={{ 
                    fontSize: '10px', 
                    padding: '4px 10px', 
                    minHeight: '26px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '4px', 
                    width: 'auto', 
                    justifyContent: 'center',
                    whiteSpace: 'nowrap'
                  }} 
                  onClick={() => { setActiveModCategory(mode === 'image' ? 'Photography' : 'FX') as any; setVideoModOpen(true); }} 
                  title={`${mode === 'image' ? 'Image' : 'Cinematic'} Modifiers & Presets`}
                >
                  {mode === 'image' ? '📸 Presets' : '🎬 Presets'}
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Mode Selector - placed in the same flex flow to allow CSS reordering */}
          <div className="composer__mode-wrapper">
            <ModeSelector onOpenEnhance={onOpenEnhance} />
          </div>

          <div className="composer__model-wrapper">
            {mode !== 'code' && mode !== 'chat' && <ModelSelector />}
          </div>

          {mode === 'image' && (
            <div className="composer__resolution-wrapper">
              <select
                className="chip"
                value={imgResolution}
                onChange={e => setImgResolution(e.target.value as any)}
                style={{ fontWeight: '500', paddingRight: '22px' }}
                title="Image Resolution"
              >
                <option value="1K">1K (Standard)</option>
                <option value="2K">2K (Full HD)</option>
                <option value="4K">4K (Ultra HD)</option>
              </select>
            </div>
          )}
          {mode === 'video' && (
            <div className="composer__resolution-wrapper">
              <select
                className="chip"
                value={videoResolution}
                onChange={e => setVideoResolution(e.target.value as any)}
                style={{ fontWeight: '500', paddingRight: '22px' }}
                title="Video Resolution"
              >
                <option value="480P">480P (SD)</option>
                <option value="720P">720P (HD)</option>
                <option value="1080P">1080P (FHD)</option>
                <option value="4K">4K (UHD)</option>
              </select>
            </div>
          )}
          {mode === 'image' && (modelOverride === 'gemini-3-pro-image-preview' || modelOverride === 'gemini-3.1-flash-image-preview') && (
            <div className="composer__grounding-wrapper">
              <label className="chip cursor-pointer" style={{ display: 'flex', alignItems: 'center', gap: '6px', userSelect: 'none', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={searchEnabled}
                  onChange={e => setSearchEnabled(e.target.checked)}
                  style={{ cursor: 'pointer', accentColor: 'var(--accent)', outline: 'none' }}
                />
                <span style={{ fontSize: '10px' }}>Search Grounding</span>
              </label>
            </div>
          )}
        </div>
      </div>

      {allStagedItems.length > 0 && (
        <div 
          style={{ 
            display: 'flex', 
            gap: '10px', 
            padding: '10px 4px', 
            margin: '6px 0 10px 0', 
            overflowX: 'auto', 
            width: '100%',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            boxSizing: 'border-box'
          }}
          className="row--wrap"
        >
          <AnimatePresence initial={false}>
            {allStagedItems.map(item => (
              <StagedCard
                key={item.id}
                item={item}
                role={item.role}
                mode={mode}
                onRoleChange={(newRole) => updateItemRole(item, item.role, newRole)}
                onDelete={() => deleteItem(item, item.role)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      <div className="composer__row" style={{ display: 'flex', alignItems: expandedText ? 'flex-end' : 'center', gap: '6px', width: '100%' }}>
        <button className="btn btn--icon desktop-only" onClick={onPickFile} title="attach" style={{ flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.5 12.5 21a5 5 0 1 1-7-7l9-9a3.5 3.5 0 1 1 5 5l-9 9a2 2 0 1 1-3-3l8-8"/></svg>
        </button>
        <input ref={fileRef} type="file" accept="image/*,video/*,audio/*" multiple style={{ display: 'none' }} onChange={onFiles} />
        
        {mode === 'video' && (
          <label className="chip" title="Add sound to video" style={{ display: 'flex', alignItems: 'center', gap: '4px', userSelect: 'none', cursor: 'pointer', height: '44px', padding: '0 8px', borderRadius: 'var(--radius)', flexShrink: 0 }}>
            <input
              type="checkbox"
              checked={addSoundOption}
              onChange={e => {
                const checked = e.target.checked;
                setAddSoundOption(checked);
                if (checked) {
                  setSoundPromptOpen(true);
                }
              }}
              style={{ cursor: 'pointer', accentColor: 'var(--accent)' }}
            />
            <span style={{ fontSize: '14px', lineHeight: 1 }} aria-label="add sound to video">🔊</span>
          </label>
        )}
        
        {/* Relative wrapper for text input and internal expand button */}
        <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <textarea
            className="composer__textarea"
            placeholder={mode === 'video' ? 'Direct a shot…' : mode === 'sound' ? 'Synthesize a sound…' : mode === '3d' ? 'Render a 3D model…' : 'Describe an image…'}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !e.metaKey) { e.preventDefault(); send(); } }}
            rows={expandedText ? 6 : 1}
            style={{ 
              transition: 'height 0.2s ease, max-height 0.2s ease', 
              height: expandedText ? '140px' : '44px',
              minHeight: expandedText ? '140px' : '44px',
              maxHeight: expandedText ? '250px' : '44px',
              paddingTop: '10px',
              paddingBottom: '10px',
              paddingRight: '36px',
              width: '100%',
              boxSizing: 'border-box',
              overflowY: expandedText ? 'auto' : 'hidden',
              resize: 'none'
            }}
          />
          
          {/* Compress/Expand Button inside the text box */}
          <button 
            type="button"
            className={`btn btn--icon ${expandedText ? 'is-active' : ''}`} 
            onClick={() => setExpandedText(!expandedText)} 
            title={expandedText ? "Collapse Text Input" : "Expand Text Input"}
            style={{ 
              position: 'absolute',
              right: '9px',
              top: '9px',
              width: '26px',
              height: '26px',
              minHeight: '26px',
              color: 'var(--fg)',
              background: expandedText ? 'var(--glass-strong)' : 'transparent',
              borderColor: expandedText ? 'var(--line-strong)' : 'transparent',
              borderWidth: expandedText ? '1px' : '0',
              zIndex: 10,
              borderRadius: '50%',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {expandedText ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M4 14h6v6M20 10h-6V4M14 10l6-6M10 14l-6 6"/>
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
              </svg>
            )}
          </button>
        </div>

        <button className={`btn btn--icon ${recording ? 'is-active' : ''}`} onClick={toggleVoice} title="voice" style={{ flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="3" width="6" height="12" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/></svg>
        </button>
        
        <button className="btn btn--primary" style={{ padding: '0 12px', minWidth: '40px', justifyContent: 'center', flexShrink: 0 }} disabled={busy} onClick={send}>
          {busy ? '…' : (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(45deg)', margin: '-1px 0 0 -2px' }}>
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          )}
        </button>
      </div>

      {enhanceOpen && createPortal(
        <div 
          className="modal-backdrop" 
          onClick={() => setEnhanceOpen(false)} 
          style={{ 
            position: 'fixed', 
            inset: 0, 
            zIndex: 9999, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            background: 'rgba(5, 5, 5, 0.75)', 
            backdropFilter: 'blur(12px)', 
            padding: '16px', 
            overflowY: 'auto',
            boxSizing: 'border-box'
          }}
        >
          <div 
            className="modal glass glass--strong col" 
            style={{ 
              maxWidth: '460px', 
              width: '100%',
              margin: 'auto',
              padding: '20px',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--line-strong)',
              maxHeight: 'calc(100dvh - 32px)',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              boxSizing: 'border-box'
            }} 
            onClick={e => e.stopPropagation()}
          >
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 'bold', margin: 0, letterSpacing: '0.04em' }}>Enhance Prompt</h3>
              <button 
                className="chip animate-hover" 
                style={{ minHeight: '24px', padding: '2px 8px', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                onClick={() => setEnhanceOpen(false)}
              >
                ✕
              </button>
            </div>
            
            <p style={{ fontSize: '11px', color: 'var(--fg-dim)', margin: '0 0 4px 0', textTransform: 'none', lineHeight: '1.4' }}>
              Enriches and optimizes your prompt with cinematic descriptions.
            </p>

            {/* Model Selection Row */}
            <div className="row row--wrap" style={{ gap: 4, background: 'var(--line)', padding: 4, borderRadius: 'var(--radius-sm)' }}>
              {(['gemini', 'chatgpt', 'grok', 'claude'] as const).map(m => (
                <button
                  key={m}
                  className={`chip ${enhanceModel === m ? 'is-active' : ''}`}
                  style={{ flex: 1, fontSize: '9px', minHeight: '24px', padding: '0 4px', textAlign: 'center', justifyContent: 'center' }}
                  onClick={() => {
                    setEnhanceModel(m);
                    triggerEnhance(text, m);
                  }}
                >
                  {m}
                </button>
              ))}
            </div>

            <div style={{ position: 'relative', marginTop: 8 }}>
              <textarea
                style={{
                  width: '100%',
                  minHeight: '120px',
                  fontSize: '13px',
                  padding: '10px',
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--line)',
                  background: 'rgba(0,0,0,0.15)',
                  color: 'var(--fg)',
                  outline: 'none',
                  resize: 'none'
                }}
                value={rewrittenPrompt}
                onChange={e => setRewrittenPrompt(e.target.value)}
                placeholder="Detailed rewrite will appear here..."
              />
              {enhancing && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(0, 0, 0, 0.45)',
                  backdropFilter: 'blur(2px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'var(--radius)',
                  color: '#fff',
                  fontSize: '11px'
                }}>
                  Enriching using {enhanceModel}...
                </div>
              )}
            </div>

            {enhanceError && (
              <span style={{ fontSize: '11px', color: 'var(--danger)' }}>{enhanceError}</span>
            )}

            <div className="row" style={{ gap: 8, marginTop: 12 }}>
              <button
                className="btn"
                style={{ flex: 1, fontSize: '11px', minHeight: '34px', padding: '0 12px' }}
                onClick={() => triggerEnhance(text, enhanceModel)}
                disabled={enhancing}
              >
                Re-Generate
              </button>
              <button
                className="btn btn--primary"
                style={{ flex: 1, fontSize: '11px', minHeight: '34px', padding: '0 12px' }}
                onClick={onApplyRewrite}
                disabled={enhancing || !rewrittenPrompt.trim()}
              >
                Inject & Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {videoModOpen && createPortal(
        <div 
          className="modal-backdrop" 
          onClick={() => setVideoModOpen(false)} 
          style={{ 
            position: 'fixed', 
            inset: 0, 
            zIndex: 9999, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            background: 'rgba(5, 5, 5, 0.75)', 
            backdropFilter: 'blur(12px)', 
            padding: '16px', 
            overflowY: 'auto',
            boxSizing: 'border-box'
          }}
        >
          <div 
            className="modal glass glass--strong col" 
            style={{ 
              maxWidth: '480px', 
              width: '100%',
              margin: 'auto',
              padding: '20px',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--line-strong)',
              maxHeight: 'calc(100dvh - 32px)',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              boxSizing: 'border-box',
              position: 'relative'
            }} 
            onClick={e => e.stopPropagation()}
          >
            {/* Soft floating dynamic notification toast */}
            <AnimatePresence>
              {presetNotification && (
                <motion.div
                  key="preset-toast-notification"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  style={{
                    position: 'absolute',
                    top: '12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--fg)',
                    color: 'var(--bg)',
                    border: '1px solid var(--line-strong)',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    padding: '5px 16px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.35)',
                    zIndex: 100000,
                    pointerEvents: 'none',
                    letterSpacing: '0.05em'
                  }}
                >
                  Saved!
                </motion.div>
              )}
            </AnimatePresence>

            {/* Header */}
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 'bold', margin: 0, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {mode === 'image' ? '📸 Image' : '🎬 Cinematic'} {activeModCategory} Presets
              </h3>
              <button 
                className="chip animate-hover" 
                style={{ minHeight: '24px', padding: '2px 8px', fontSize: '10px' }} 
                onClick={() => setVideoModOpen(false)}
              >
                ✕
              </button>
            </div>

            {/* In-modal Category Switcher */}
            <div className="row" style={{ gap: 4, background: 'var(--line)', padding: 4, borderRadius: 'var(--radius-sm)', width: '100%' }}>
              {((mode === 'image' ? Object.keys(IMAGE_PRESETS) : Object.keys(VIDEO_PRESETS)) as any[]).map((cat) => (
                <button
                  key={cat}
                  className={`chip ${activeModCategory === cat ? 'is-active' : ''}`}
                  style={{ flex: 1, fontSize: '9px', minHeight: '24px', padding: 0, textAlign: 'center', justifyContent: 'center' }}
                  onClick={() => {
                    setActiveModCategory(cat as any);
                    setCustomModValue('');
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Custom Input Enhancer */}
            <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px', borderRadius: 'var(--radius)', border: '1px solid var(--line)' }}>
              <span style={{ fontSize: '9px', color: 'var(--fg-dim)', display: 'block', marginBottom: '6px' }}>Input custom {activeModCategory.toLowerCase()}:</span>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="text"
                  placeholder={`e.g. dramatic floating debris...`}
                  value={customModValue}
                  onChange={e => setCustomModValue(e.target.value)}
                  style={{
                    flex: 1,
                    background: 'rgba(0,0,0,0.2)',
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '6px 8px',
                    fontSize: '11px',
                    color: 'var(--fg)',
                    outline: 'none'
                  }}
                />
                <button
                  className="btn btn--primary"
                  style={{ fontSize: '10px', padding: '0 10px', minHeight: '28px' }}
                  onClick={() => {
                    if (customModValue.trim()) {
                      handleAddTagToPrompt(customModValue.trim());
                      setCustomModValue('');
                    }
                  }}
                >
                  Inject
                </button>
              </div>

              {/* In-modal custom enhance selectors */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <span style={{ fontSize: '8px', color: 'var(--fg-dim)' }}>Enhancer:</span>
                  <select 
                    style={{ fontSize: '8px', padding: '2px 4px', minHeight: '18px', background: 'transparent', border: '1px solid var(--line)', borderRadius: '3px', color: 'var(--fg-dim)' }}
                    value={modEnhanceModel}
                    onChange={e => setModEnhanceModel(e.target.value as any)}
                  >
                    <option value="gemini">Gemini</option>
                    <option value="chatgpt">ChatGPT</option>
                    <option value="grok">Grok</option>
                    <option value="claude">Claude</option>
                  </select>
                </div>
                <button
                  className="btn"
                  style={{ fontSize: '9px', minHeight: '20px', padding: '2px 6px', opacity: customModValue.trim() ? 1 : 0.5 }}
                  disabled={enhancingMod || !customModValue.trim()}
                  onClick={enhanceCustomMod}
                >
                  {enhancingMod ? 'Enhancing...' : '✦ Enhance Text'}
                </button>
              </div>
            </div>

            {/* Presets List */}
            <span style={{ fontSize: '9.5px', color: 'var(--fg-dim)', fontWeight: 'bold' }}>20 Masterclass Presets (Click to add/remove from prompt):</span>
            
            <div 
              style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '6px', 
                maxHeight: '220px', 
                overflowY: 'auto',
                paddingRight: '4px'
              }}
            >
              {((mode === 'image' ? IMAGE_PRESETS : VIDEO_PRESETS) as any)[activeModCategory]?.map((item: any) => {
                const tag = `[${activeModCategory}: ${item.inject}]`;
                const active = text.includes(tag);
                return (
                  <button
                    key={item.label}
                    className={`chip animate-hover`}
                    type="button"
                    onClick={() => handleAddTagToPrompt(item.inject)}
                    style={{
                      justifyContent: 'flex-start',
                      fontSize: '10px',
                      padding: '8px 10px',
                      borderRadius: 'var(--radius)',
                      background: active ? 'rgba(230, 180, 50, 0.15)' : 'rgba(255,255,255,0.01)',
                      border: active ? '1px solid rgba(230, 180, 50, 0.6)' : '1px solid var(--line)',
                      color: active ? '#ffdd88' : 'var(--fg)',
                      textAlign: 'left',
                      whiteSpace: 'normal',
                      lineHeight: '1.2',
                      minHeight: '44px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ fontWeight: 'bold', fontSize: '10.5px' }}>{item.label}</span>
                      <span style={{ fontSize: '8.5px', opacity: 0.6, width: '100%', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {item.inject}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Injected count helper */}
            <div className="row" style={{ justifyContent: 'space-between', borderTop: '1px solid var(--line)', paddingTop: '8px', marginTop: '4px' }}>
              <span style={{ fontSize: '9px', color: 'var(--fg-dim)' }}>
                Organizes terms clearly for the AI Prompt Director
              </span>
              <button 
                className="btn btn--primary" 
                style={{ fontSize: '10.5px', minHeight: '28px', padding: '0 16px' }} 
                onClick={() => setVideoModOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {soundPromptOpen && createPortal(
        <div 
          className="modal-backdrop" 
          style={{ 
            position: 'fixed', 
            inset: 0, 
            zIndex: 9999, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            background: 'rgba(5, 5, 5, 0.45)', 
            backdropFilter: 'blur(8px)', 
            padding: '16px',
            boxSizing: 'border-box'
          }}
          onClick={() => setSoundPromptOpen(false)}
        >
          <div 
            className="modal col matches-theme" 
            style={{ 
              maxWidth: '345px', 
              width: '100%',
              background: 'var(--bg)',
              color: 'var(--fg)',
              border: '1px solid var(--line-strong)',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.25)',
              borderRadius: 'var(--radius-lg)',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              boxSizing: 'border-box'
            }} 
            onClick={e => e.stopPropagation()}
          >
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="sc" style={{ fontSize: '11px', fontWeight: 'bold', letterSpacing: '0.04em' }}>VIDEO SOUND EFFECTS</span>
              <button 
                className="chip animate-hover" 
                style={{ minHeight: '22px', padding: '0 6px', fontSize: '9px' }} 
                onClick={() => setSoundPromptOpen(false)}
              >
                ✕
              </button>
            </div>
            
            <p style={{ fontSize: '10px', color: 'var(--fg-dim)', margin: 0, lineHeight: '1.4' }}>
              Describe the ambient noise, music, or foley sound effects to layer with your video.
            </p>
            
            <div className="field">
              <input
                type="text"
                className="field input"
                style={{ 
                  fontSize: '12.5px', 
                  padding: '10px 12px', 
                  background: 'color-mix(in oklab, var(--fg) 5%, transparent)',
                  color: 'var(--fg)',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--radius-sm)'
                }}
                autoFocus
                value={soundPromptText}
                onChange={e => setSoundPromptText(e.target.value)}
                placeholder="e.g. ambient wind, heavy mechanical hum..."
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    setSoundPromptOpen(false);
                  }
                }}
              />
            </div>
            
            <div className="row" style={{ gap: '8px', marginTop: '4px' }}>
              <button 
                className="btn btn--ghost text-xs" 
                style={{ flex: 1, minHeight: '30px', fontSize: '10px', padding: 0 }} 
                onClick={() => {
                  setAddSoundOption(false);
                  setSoundPromptOpen(false);
                }}
              >
                disable sound
              </button>
              <button 
                className="btn btn--primary text-xs" 
                style={{ flex: 1, minHeight: '30px', fontSize: '10px', padding: 0 }} 
                onClick={() => setSoundPromptOpen(false)}
              >
                set sound prompt
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}

function directorSummary(decision: { brief: string; recommended: { reason: string } }, model: string, provider: string): string {
  return `Routed to ${provider}/${model} — ${decision.recommended.reason}\n\n${decision.brief}`;
}

interface StagedCardProps {
  item: Attachment;
  role: 'start' | 'end' | 'ref';
  onRoleChange: (newRole: 'start' | 'end' | 'ref') => void;
  onDelete: () => void;
  mode: string;
}

function StagedCard({ item, role, onRoleChange, onDelete, mode }: StagedCardProps) {
  const [url, setUrl] = useState<string>('');

  useEffect(() => {
    const objectUrl = URL.createObjectURL(item.blob);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [item.blob]);

  const isStart = role === 'start';
  const isEnd = role === 'end';
  const isRef = role === 'ref';

  let borderColor = 'var(--line-strong)';
  let glowColor = 'transparent';
  let badgeText = '';
  let badgeColor = 'transparent';

  if (item.kind === 'image' && mode === 'video') {
    if (isStart) {
      borderColor = 'rgba(230, 180, 50, 0.85)';
      glowColor = 'rgba(230, 180, 50, 0.25)';
      badgeText = 'START';
      badgeColor = '#e6b432';
    } else if (isEnd) {
      borderColor = 'rgba(180, 60, 220, 0.85)';
      glowColor = 'rgba(180, 60, 220, 0.25)';
      badgeText = 'END';
      badgeColor = '#b43ce6';
    } else if (isRef) {
      borderColor = 'var(--accent, #3b82f6)';
      glowColor = 'rgba(59, 130, 246, 0.2)';
      badgeText = 'REF';
      badgeColor = 'var(--accent, #3b82f6)';
    }
  } else if (item.kind === 'image') {
    borderColor = 'var(--accent, #3b82f6)';
    glowColor = 'rgba(59, 130, 246, 0.1)';
    badgeText = 'IMAGE';
    badgeColor = 'var(--accent, #3b82f6)';
  } else {
    badgeText = item.kind.toUpperCase();
    badgeColor = 'var(--accent, #3b82f6)';
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: -10 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className="glass"
      style={{
        width: '100px',
        height: '118px',
        borderRadius: '8px',
        padding: '5px',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        border: `1px solid ${borderColor}`,
        boxShadow: `0 0 10px ${glowColor}`,
        background: 'rgba(255, 255, 255, 0.04)',
        boxSizing: 'border-box'
      }}
    >
      {/* Dynamic role badge */}
      {badgeText && (
        <span
          style={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            background: badgeColor,
            color: '#fff',
            fontSize: '6.5px',
            fontWeight: 'bold',
            padding: '1px 3.5px',
            borderRadius: '3px',
            zIndex: 8,
            textShadow: '0 1px 1px rgba(0,0,0,0.5)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
            pointerEvents: 'none',
            letterSpacing: '0.04em'
          }}
        >
          {badgeText}
        </span>
      )}

      {/* Delete trigger */}
      <button
        type="button"
        onClick={onDelete}
        style={{
          position: 'absolute',
          top: '-4px',
          right: '-4px',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          background: 'rgba(220, 50, 50, 0.95)',
          color: '#fff',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '10px',
          cursor: 'pointer',
          zIndex: 10,
          boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
          outline: 'none'
        }}
      >
        ✕
      </button>

      {/* Visual Preview */}
      <div style={{ width: '100%', height: '64px', borderRadius: '4px', overflow: 'hidden', background: '#09090b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {item.kind === 'image' && url && (
          <img src={url} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
        {item.kind === 'video' && url && (
          <video src={url} muted playsInline autoPlay loop style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
        {item.kind === 'audio' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--accent)' }}>
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
              <path d="M19 10v1a7 7 0 0 1-14 0v-1M12 19v4M8 23h8"/>
            </svg>
            <span style={{ fontSize: '7px', opacity: 0.6, width: '100%', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '80px', textAlign: 'center' }}>{item.name}</span>
          </div>
        )}
      </div>

      {/* Role Assign Pills */}
      <div style={{ marginTop: '3px' }}>
        {item.kind === 'image' && mode === 'video' ? (
          <div style={{ display: 'flex', background: 'rgba(0,0,0,0.3)', padding: '1px', borderRadius: '4px', gap: '1px' }}>
            {(['start', 'end', 'ref'] as const).map(r => {
              const active = role === r;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => onRoleChange(r)}
                  style={{
                    flex: 1,
                    fontSize: '8px',
                    padding: '2px 0',
                    border: 'none',
                    borderRadius: '2.5px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: active ? 'var(--accent, #3b82f6)' : 'transparent',
                    color: active ? '#fff' : 'var(--fg-dim)',
                    fontWeight: active ? 'bold' : 'normal',
                    transition: 'all 0.1s ease',
                    outline: 'none'
                  }}
                >
                  {r === 'start' ? 'Start' : r === 'end' ? 'End' : 'Ref'}
                </button>
              );
            })}
          </div>
        ) : (
          <div style={{ fontSize: '8px', textAlign: 'center', color: 'var(--accent)', fontWeight: 'bold', background: 'rgba(0,0,0,0.3)', padding: '2px 4px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {item.kind === 'image' ? 'Ref Image' : item.kind}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function FXIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275z"/>
    </svg>
  );
}

function CameraAngleIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="14 3 21 3 21 10"/>
      <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  );
}

function LightingIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4"/>
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
    </svg>
  );
}

function MotionIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m5 17 5-5-5-5M19 12H9"/>
    </svg>
  );
}

function StyleIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 14.7255 3.09032 17.1962 4.85857 19H12V22Z" />
      <circle cx="7.5" cy="10.5" r="1.5" />
      <circle cx="11.5" cy="7.5" r="1.5" />
      <circle cx="16.5" cy="9.5" r="1.5" />
    </svg>
  );
}
